// import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { linktivity, LinktivityStamp } from '@link/share/icons';
import { formatPrice } from '@link/share/utils';
import styles from './prePayment.module.css';

interface IBankInfoItem {
  label: string;
  value: string;
}

// 日元口座（默认）
const JPY_BANK_INFO: IBankInfoItem[] = [
  { label: 'Account Name', value: 'LINKTIVITY Inc. / リンクティビティ インク' },
  { label: 'Bank Name', value: 'MUFG BANK,LTD. / 三菱UFJ銀行' },
  { label: 'Branch Name', value: 'Aoyamadori Branch / 青山通支店' },
  {
    label: 'Bank Address',
    value:
      '1-1-1, Minamiaoyama, Minato-ku, Tokyo, Japan / 東京都港区南青山1-1-1'
  },
  { label: 'Account Number', value: '0400266' },
  { label: 'Swift code', value: 'BOTKJPJT' },
  { label: 'Bank code', value: '0005' },
  { label: 'Branch code', value: '084' }
];

// 美元口座
const USD_BANK_INFO: IBankInfoItem[] = [
  { label: 'Bank Name', value: 'MUFG BANK,LTD.' },
  { label: 'Branch Name', value: 'Aoyamadori Branch' },
  {
    label: 'Bank Address',
    value: '1-1-1, Minamiaoyama, Minato-ku, Tokyo, Japan'
  },
  { label: 'Account Number', value: '084-0118169' },
  { label: 'Account Name', value: 'LINKTIVITY Inc.' },
  {
    label: 'Beneficiary Address',
    value:
      'Hibiya Park Front 19F, 2-1-6 Uchisaiwaicho, Chiyoda-ku, Tokyo, Japan'
  },
  { label: 'Swift code', value: 'BOTKJPJT' }
];

// 韩元口座
const KRW_BANK_INFO: IBankInfoItem[] = [
  { label: 'Bank Name', value: 'KEB Hana Bank' },
  { label: 'Branch Name', value: 'Jonro Banking Center' },
  { label: 'Beneficiary Name', value: 'LINKTIVITY Korea Inc.' },
  {
    label: 'Bank Address',
    value: '11F, 26, Ujeongguk-ro, Jongno-gu, Seoul, Republic of Korea, 03161'
  },
  { label: 'Account Number', value: '231-910051-44004' },
  { label: 'Swift code', value: 'KOEXKRSE' }
];

const BANK_INFO_MAP: Record<string, IBankInfoItem[]> = {
  JPY: JPY_BANK_INFO,
  USD: USD_BANK_INFO,
  KRW: KRW_BANK_INFO
};

const PrePayment = () => {
  // const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId') as string;
  const agentName = searchParams.get('agentName') as string;
  const payTime = searchParams.get('payTime') as string;
  const currency = searchParams.get('currency') as string;
  const amount = Number(searchParams.get('amount'));
  const address = searchParams.get('address') as string;
  const bankInfo = BANK_INFO_MAP[currency] ?? JPY_BANK_INFO;

  const now = new Date();
  const billNumberSuffix =
    now.toISOString().slice(0, 19).replace(/[-:]/g, '') + 'Z';
  const billNumber = agentId
    ? `${agentId}-${billNumberSuffix}`
    : `{OTA_ID}-${billNumberSuffix}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.invoice}>
        <div className={styles.header}>
          <div className={styles.companyInfo}>
            <img src={linktivity} className={styles.logo} />
            <h2 className={styles.companyName}>LINKTIVITY Inc.</h2>
            <div className={styles.address}>
              <p>Hibiya Park Front 19F,</p>
              <p>2-1-6 Uchisaiwaicho, Chiyoda, Tokyo,</p>
              <p>100-0011, Japan</p>
            </div>
          </div>
          <div className={styles.invoiceTitle}>
            <h1>Invoice</h1>
            <div className={styles.stamp}>
              <p className={styles.issued}>
                Issued:
                {new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric'
                })}
              </p>
              <p className={styles.paymentDue}>
                Payment Due:
                {new Date(payTime).toLocaleDateString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric'
                })}
              </p>
              <img src={LinktivityStamp} className={styles.stampImg} />
            </div>
          </div>
        </div>

        <div className={styles.billTo}>
          <h3>Bill To: {agentName}</h3>
          <p className={styles.billNumber}>Bill Number: {billNumber}</p>
          <p className={styles.billAddress}>Address: {address}</p>
        </div>

        {/* Items Table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.descriptionCol}>Item Description</th>
                <th className={styles.quantityCol}>Quantity</th>
                <th className={styles.amountCol}>Amount ({currency})</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Prepayment</td>
                <td className={styles.right}>1</td>
                <td className={styles.right}>{formatPrice(amount)}</td>
              </tr>
            </tbody>
          </table>

          <div className={styles.totals}>
            <div className={`${styles.totalRow} ${styles.grandTotal}`}>
              <span className={styles.label}>Total</span>
              <span className={styles.value}>{formatPrice(amount)}</span>
            </div>
          </div>
        </div>

        <div className={styles.bankDetails}>
          <h3 className={styles.bankTitle}>Bank Details</h3>
          <div className={styles.bankInfo}>
            {bankInfo.map(({ label, value }) => (
              <p key={label}>
                <strong>{label}:</strong> {value}
              </p>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          <p>
            ★Thank you for your business! Payment is expected within payment due
            date; please process this invoice within that time.
          </p>
          <p>
            ★Bank transfer fee is not included in this amount. Please burden
            this at your expense.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrePayment;
